import React, { useState, useEffect } from 'react';
import {
    Users,
    CreditCard,
    Shield,
    Ban,
    AlertTriangle,
    Eye,
    Search,
    Filter,
    Settings,
    BarChart3,
    Download,
    RefreshCw,
    UserCheck,
    UserX,
    DollarSign,
    Calendar,
    Activity,
    Database,
    Mail,
    ExternalLink,
    Trash2,
    Edit3,
    Lock,
    Unlock,
    X,
    Flag,
} from 'lucide-react';
import {ShieldCheckIcon} from "@heroicons/react/24/outline";
import ModerationDashboard from "../components/admin/ModerationDashboard.jsx";

const AdminPortal = () => {
    const [activeTab, setActiveTab] = useState('users');
    const [users, setUsers] = useState([]);
    const [payments, setPayments] = useState([]);
    const [analytics, setAnalytics] = useState({});
    const [loading, setLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [filters, setFilters] = useState({
        plan: 'all',
        status: 'all',
        dateRange: 'all'
    });
    const [selectedUser, setSelectedUser] = useState(null);
    const [userDetails, setUserDetails] = useState(null);
    const [userPosts, setUserPosts] = useState([]);
    const [showUserModal, setShowUserModal] = useState(false);
    const [loadingUserDetails, setLoadingUserDetails] = useState(false);

    // Load admin data from backend APIs
    useEffect(() => {
        loadAdminData();
    }, []);

    const getAuthHeaders = () => {
        const token = localStorage.getItem('token') || sessionStorage.getItem('token');
        return {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
        };
    };

    const loadAdminData = async () => {
        setLoading(true);
        try {
            // Load dashboard analytics
            const dashboardResponse = await fetch('/api/admin/dashboard', {
                headers: getAuthHeaders()
            });

            if (dashboardResponse.ok) {
                const dashboardData = await dashboardResponse.json();
                setAnalytics(dashboardData.data);
            }

            // Load users
            const usersResponse = await fetch('/api/admin/users?limit=50', {
                headers: getAuthHeaders()
            });

            if (usersResponse.ok) {
                const usersData = await usersResponse.json();
                setUsers(usersData.data.users);
            }

            // Load payments
            const paymentsResponse = await fetch('/api/admin/payments?limit=50', {
                headers: getAuthHeaders()
            });

            if (paymentsResponse.ok) {
                const paymentsData = await paymentsResponse.json();
                setPayments(paymentsData.data.payments.map(payment => ({
                    id: payment.id,
                    userEmail: payment.userEmail,
                    amount: payment.amount,
                    status: payment.status,
                    date: new Date(payment.created).toLocaleDateString(),
                    stripeId: payment.id
                })));
            }

            setLoading(false);
        } catch (error) {
            console.error('Error loading admin data:', error);
            setLoading(false);
        }
    };

    const handleUserAction = async (userId, action) => {
        try {
            let endpoint = '';
            let body = {};

            switch (action) {
                case 'block':
                    endpoint = `/api/admin/users/${userId}/block`;
                    body = { block: true };
                    break;
                case 'unblock':
                    endpoint = `/api/admin/users/${userId}/block`;
                    body = { block: false };
                    break;
                case 'flag':
                    endpoint = `/api/admin/users/${userId}/flag`;
                    body = { flag: true };
                    break;
                case 'unflag':
                    endpoint = `/api/admin/users/${userId}/flag`;
                    body = { flag: false };
                    break;
                case 'view':
                    await openUserDetailView(userId);
                    return;
                default:
                    console.warn(`Unknown action: ${action}`);
                    return;
            }

            const response = await fetch(endpoint, {
                method: 'POST',
                headers: getAuthHeaders(),
                body: JSON.stringify(body)
            });

            if (response.ok) {
                const result = await response.json();
                console.log(`✅ ${action} successful:`, result);

                // Update local state
                setUsers(users.map(user => {
                    if (user._id === userId) {
                        switch (action) {
                            case 'block':
                                return { ...user, isBlocked: true, status: 'blocked' };
                            case 'unblock':
                                return { ...user, isBlocked: false, status: 'active' };
                            case 'flag':
                                return { ...user, isFlagged: true };
                            case 'unflag':
                                return { ...user, isFlagged: false };
                            default:
                                return user;
                        }
                    }
                    return user;
                }));

                // Show success message
                alert(`User ${action}ed successfully`);
            } else {
                const error = await response.json();
                throw new Error(error.message || `Failed to ${action} user`);
            }
        } catch (error) {
            console.error(`Error ${action}ing user:`, error);
            alert(`Failed to ${action} user: ${error.message}`);
        }
    };

    const openUserDetailView = async (userId) => {
        try {
            setLoadingUserDetails(true);
            setShowUserModal(true);

            // Find user in current list for basic info
            const user = users.find(u => u._id === userId || u.id === userId);
            setSelectedUser(user);

            // Fetch detailed user data and posts
            const [userResponse, postsResponse] = await Promise.all([
                fetch(`/api/admin/users/${userId}/details`, {
                    headers: getAuthHeaders()
                }),
                fetch(`/api/admin/users/${userId}/posts?limit=50`, {
                    headers: getAuthHeaders()
                })
            ]);

            if (userResponse.ok) {
                const userData = await userResponse.json();
                setUserDetails(userData.data);
            }

            if (postsResponse.ok) {
                const postsData = await postsResponse.json();
                setUserPosts(postsData.data);
            }

            setLoadingUserDetails(false);
        } catch (error) {
            console.error('Error loading user details:', error);
            setLoadingUserDetails(false);
            alert('Failed to load user details');
        }
    };

    // User Detail Modal Component
    const UserDetailModal = () => {
        if (!showUserModal) return null;

        const closeModal = () => {
            setShowUserModal(false);
            setSelectedUser(null);
            setUserDetails(null);
            setUserPosts([]);
        };

        return (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                <div className="bg-white rounded-lg max-w-6xl w-full max-h-[90vh] overflow-y-auto">
                    <div className="p-6 border-b border-gray-200 flex justify-between items-center">
                        <h2 className="text-2xl font-bold text-gray-900">
                            User Audit: {selectedUser?.email}
                        </h2>
                        <button
                            onClick={closeModal}
                            className="text-gray-500 hover:text-gray-700"
                        >
                            <X className="w-6 h-6" />
                        </button>
                    </div>

                    {loadingUserDetails ? (
                        <div className="p-8 text-center">
                            <RefreshCw className="w-8 h-8 animate-spin mx-auto mb-4" />
                            <p>Loading user details...</p>
                        </div>
                    ) : (
                        <div className="p-6 space-y-6">
                            {/* User Overview */}
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                                <div className="bg-gray-50 p-4 rounded-lg">
                                    <h3 className="font-semibold text-gray-900 mb-2">Account Info</h3>
                                    <div className="space-y-1 text-sm">
                                        <p><span className="font-medium">Name:</span> {selectedUser?.firstName} {selectedUser?.lastName}</p>
                                        <p><span className="font-medium">Email:</span> {selectedUser?.email}</p>
                                        <p><span className="font-medium">Plan:</span> {selectedUser?.plan}</p>
                                        <p><span className="font-medium">Status:</span> {selectedUser?.status}</p>
                                        <p><span className="font-medium">Joined:</span> {new Date(selectedUser?.createdAt).toLocaleDateString()}</p>
                                    </div>
                                </div>

                                <div className="bg-gray-50 p-4 rounded-lg">
                                    <h3 className="font-semibold text-gray-900 mb-2">Usage Stats</h3>
                                    <div className="space-y-1 text-sm">
                                        <p><span className="font-medium">Posts Generated:</span> {userDetails?.usageStats?.postsGenerated || 0}</p>
                                        <p><span className="font-medium">Posts Published:</span> {userDetails?.usageStats?.postsPublished || 0}</p>
                                        <p><span className="font-medium">Last Reset:</span> {userDetails?.usageStats?.lastResetDate ? new Date(userDetails.usageStats.lastResetDate).toLocaleDateString() : 'N/A'}</p>
                                        <p><span className="font-medium">Last Login:</span> {selectedUser?.lastLogin ? new Date(selectedUser.lastLogin).toLocaleDateString() : 'Never'}</p>
                                    </div>
                                </div>

                                <div className="bg-gray-50 p-4 rounded-lg">
                                    <h3 className="font-semibold text-gray-900 mb-2">Account Flags</h3>
                                    <div className="space-y-2">
                                        <div className={`inline-flex items-center px-2 py-1 rounded-full text-xs ${selectedUser?.isBlocked ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'}`}>
                                            {selectedUser?.isBlocked ? 'Blocked' : 'Active'}
                                        </div>
                                        {selectedUser?.isFlagged && (
                                            <div className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-yellow-100 text-yellow-800 ml-2">
                                                Flagged
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>

                            {/* Posts History */}
                            <div>
                                <h3 className="text-lg font-semibold text-gray-900 mb-4">
                                    Posts History ({userPosts.length})
                                </h3>
                                <div className="bg-white border rounded-lg overflow-hidden">
                                    <div className="max-h-96 overflow-y-auto">
                                        <table className="min-w-full divide-y divide-gray-200">
                                            <thead className="bg-gray-50 sticky top-0">
                                            <tr>
                                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Content</th>
                                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Platforms</th>
                                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Engagement</th>
                                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Created</th>
                                            </tr>
                                            </thead>
                                            <tbody className="divide-y divide-gray-200">
                                            {userPosts.map((post) => (
                                                <tr key={post._id} className="hover:bg-gray-50">
                                                    <td className="px-4 py-2">
                                                        <div className="max-w-xs">
                                                            <p className="text-sm font-medium text-gray-900 truncate">
                                                                {post.title || 'Untitled'}
                                                            </p>
                                                            <p className="text-xs text-gray-500 truncate">
                                                                {post.caption?.substring(0, 100)}...
                                                            </p>
                                                        </div>
                                                    </td>
                                                    <td className="px-4 py-2">
                                                        <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                                                            post.status === 'posted' ? 'bg-green-100 text-green-800' :
                                                                post.status === 'failed' ? 'bg-red-100 text-red-800' :
                                                                    post.status === 'scheduled' ? 'bg-blue-100 text-blue-800' :
                                                                        'bg-gray-100 text-gray-800'
                                                        }`}>
                                                            {post.status}
                                                        </span>
                                                    </td>
                                                    <td className="px-4 py-2">
                                                        <div className="flex flex-wrap gap-1">
                                                            {post.targetPlatforms?.map((platform) => (
                                                                <span key={platform} className="inline-flex px-1 py-0.5 text-xs bg-gray-100 text-gray-700 rounded">
                                                                    {platform}
                                                                </span>
                                                            ))}
                                                        </div>
                                                    </td>
                                                    <td className="px-4 py-2 text-xs text-gray-500">
                                                        {post.engagement ? (
                                                            <div>
                                                                <div>👁 {post.engagement.impressions || 0}</div>
                                                                <div>❤️ {post.engagement.likes || 0}</div>
                                                            </div>
                                                        ) : '-'}
                                                    </td>
                                                    <td className="px-4 py-2 text-xs text-gray-500">
                                                        {new Date(post.createdAt).toLocaleDateString()}
                                                    </td>
                                                </tr>
                                            ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        );
    };

    const exportData = (type) => {
        const data = type === 'users' ? users : payments;
        const csv = convertToCSV(data);
        downloadCSV(csv, `${type}_export_${new Date().toISOString().split('T')[0]}.csv`);
    };

    const convertToCSV = (data) => {
        if (!data.length) return '';

        const headers = Object.keys(data[0]).join(',');
        const rows = data.map(row =>
            Object.values(row).map(value =>
                typeof value === 'string' ? `"${value}"` : value
            ).join(',')
        );

        return [headers, ...rows].join('\n');
    };

    const downloadCSV = (csv, filename) => {
        const blob = new Blob([csv], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        a.click();
        window.URL.revokeObjectURL(url);
    };

    const filteredUsers = users.filter(user => {
        const matchesSearch = user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
            user.firstName.toLowerCase().includes(searchTerm.toLowerCase()) ||
            user.lastName.toLowerCase().includes(searchTerm.toLowerCase());
        const matchesPlan = filters.plan === 'all' || user.plan === filters.plan;
        const matchesStatus = filters.status === 'all' || user.status === filters.status;

        return matchesSearch && matchesPlan && matchesStatus;
    });

    const StatCard = ({ title, value, icon: Icon, color = 'blue' }) => (
        <div className="bg-white rounded-lg shadow-md p-6 border-l-4 border-l-blue-500">
            <div className="flex items-center justify-between">
                <div>
                    <p className="text-sm text-gray-600 mb-1">{title}</p>
                    <p className="text-2xl font-bold text-gray-900">{value}</p>
                </div>
                <Icon className={`w-8 h-8 text-${color}-500`} />
            </div>
        </div>
    );

    const UserRow = ({ user }) => (
        <tr className="hover:bg-gray-50 border-b">
            <td className="px-6 py-4">
                <div className="flex items-center">
                    <div className="flex-shrink-0 w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
            <span className="text-blue-600 font-medium">
              {user.firstName?.[0]}{user.lastName?.[0]}
            </span>
                    </div>
                    <div className="ml-4">
                        <div className="text-sm font-medium text-gray-900">
                            {user.fullName || `${user.firstName} ${user.lastName}`}
                        </div>
                        <div className="text-sm text-gray-500">{user.email}</div>
                    </div>
                </div>
            </td>
            <td className="px-6 py-4">
        <span className={`px-2 py-1 text-xs font-medium rounded-full ${
            user.plan === 'enterprise' ? 'bg-purple-100 text-purple-800' :
                user.plan === 'pro' ? 'bg-blue-100 text-blue-800' :
                    'bg-gray-100 text-gray-800'
        }`}>
          {user.plan}
        </span>
            </td>
            <td className="px-6 py-4">
        <span className={`px-2 py-1 text-xs font-medium rounded-full ${
            user.status === 'active' ? 'bg-green-100 text-green-800' :
                user.status === 'blocked' ? 'bg-red-100 text-red-800' :
                    'bg-yellow-100 text-yellow-800'
        }`}>
          {user.status}
        </span>
            </td>
            <td className="px-6 py-4 text-sm text-gray-900">
                {user.postsGenerated}
            </td>
            <td className="px-6 py-4 text-sm text-gray-500">
                {user.lastLoginFormatted || user.lastLogin}
            </td>
            <td className="px-6 py-4">
                <div className="flex items-center space-x-2">
                    {user.isFlagged && (
                        <AlertTriangle className="w-4 h-4 text-yellow-500" title="Flagged" />
                    )}
                    {user.isBlocked && (
                        <Ban className="w-4 h-4 text-red-500" title="Blocked" />
                    )}
                </div>
            </td>
            <td className="px-6 py-4">
                <div className="flex items-center space-x-2">
                    <button
                        onClick={() => handleUserAction(user._id || user.id, 'view')}
                        className="text-blue-600 hover:text-blue-800"
                        title="View Details"
                    >
                        <Eye className="w-4 h-4" />
                    </button>
                    <button
                        onClick={() => handleUserAction(user._id || user.id, user.isBlocked ? 'unblock' : 'block')}
                        className={`${user.isBlocked ? 'text-green-600 hover:text-green-800' : 'text-red-600 hover:text-red-800'}`}
                        title={user.isBlocked ? 'Unblock' : 'Block'}
                    >
                        {user.isBlocked ? <Unlock className="w-4 h-4" /> : <Lock className="w-4 h-4" />}
                    </button>
                    <button
                        onClick={() => handleUserAction(user._id || user.id, user.isFlagged ? 'unflag' : 'flag')}
                        className={`${user.isFlagged ? 'text-green-600 hover:text-green-800' : 'text-yellow-600 hover:text-yellow-800'}`}
                        title={user.isFlagged ? 'Remove Flag' : 'Flag User'}
                    >
                        <AlertTriangle className="w-4 h-4" />
                    </button>
                </div>
            </td>
        </tr>
    );

    const PaymentRow = ({ payment }) => {
        const getStripeUrl = (paymentId) => {
            const isProduction = process.env.NODE_ENV === 'production';
            const baseUrl = isProduction
                ? 'https://dashboard.stripe.com/payments/'
                : 'https://dashboard.stripe.com/test/payments/';
            return `${baseUrl}${paymentId}`;
        };

        return (
            <tr className="hover:bg-gray-50 border-b">
                <td className="px-6 py-4 text-sm text-gray-900">{payment.id}</td>
                <td className="px-6 py-4 text-sm text-gray-900">{payment.userEmail}</td>
                <td className="px-6 py-4 text-sm text-gray-900">${(payment.amount / 100).toFixed(2)}</td>
                <td className="px-6 py-4">
          <span className={`px-2 py-1 text-xs font-medium rounded-full ${
              payment.plan === 'enterprise' ? 'bg-purple-100 text-purple-800' :
                  payment.plan === 'pro' ? 'bg-blue-100 text-blue-800' :
                      'bg-gray-100 text-gray-800'
          }`}>
            {payment.plan}
          </span>
                </td>
                <td className="px-6 py-4">
          <span className={`px-2 py-1 text-xs font-medium rounded-full ${
              payment.status === 'succeeded' ? 'bg-green-100 text-green-800' :
                  payment.status === 'failed' ? 'bg-red-100 text-red-800' :
                      'bg-yellow-100 text-yellow-800'
          }`}>
            {payment.status}
          </span>
                </td>
                <td className="px-6 py-4 text-sm text-gray-500">{payment.date}</td>
                <td className="px-6 py-4">
                    <button
                        onClick={() => window.open(getStripeUrl(payment.stripeId), '_blank')}
                        className="text-blue-600 hover:text-blue-800"
                        title={`View in Stripe ${process.env.NODE_ENV === 'production' ? '(Live)' : '(Test)'}`}
                    >
                        <ExternalLink className="w-4 h-4" />
                    </button>
                </td>
            </tr>
        );
    };

    return (
        <div className="min-h-screen bg-gray-50">
            {/* Header */}
            <div className="bg-white shadow">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex justify-between items-center py-6">
                        <div className="flex items-center">
                            <Shield className="w-8 h-8 text-blue-600 mr-3" />
                            <h1 className="text-2xl font-bold text-gray-900">PostMgr Admin Portal</h1>
                        </div>
                        <div className="flex items-center space-x-4">
                            <button
                                onClick={loadAdminData}
                                className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                                disabled={loading}
                            >
                                <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                                Refresh
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Navigation Tabs */}
            <div className="bg-white border-b">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <nav className="flex space-x-8">
                        <button
                            onClick={() => setActiveTab('dashboard')}
                            className={`py-4 px-1 border-b-2 font-medium text-sm ${
                                activeTab === 'dashboard'
                                    ? 'border-blue-500 text-blue-600'
                                    : 'border-transparent text-gray-500 hover:text-gray-700'
                            }`}
                        >
                            <BarChart3 className="w-4 h-4 inline mr-2" />
                            Dashboard
                        </button>
                        <button
                            onClick={() => setActiveTab('users')}
                            className={`py-4 px-1 border-b-2 font-medium text-sm ${
                                activeTab === 'users'
                                    ? 'border-blue-500 text-blue-600'
                                    : 'border-transparent text-gray-500 hover:text-gray-700'
                            }`}
                        >
                            <Users className="w-4 h-4 inline mr-2" />
                            Users
                        </button>
                        <button
                            onClick={() => setActiveTab('payments')}
                            className={`py-4 px-1 border-b-2 font-medium text-sm ${
                                activeTab === 'payments'
                                    ? 'border-blue-500 text-blue-600'
                                    : 'border-transparent text-gray-500 hover:text-gray-700'
                            }`}
                        >
                            <CreditCard className="w-4 h-4 inline mr-2" />
                            Payments
                        </button>
                        <button
                            onClick={() => setActiveTab('system')}
                            className={`py-4 px-1 border-b-2 font-medium text-sm ${
                                activeTab === 'system'
                                    ? 'border-blue-500 text-blue-600'
                                    : 'border-transparent text-gray-500 hover:text-gray-700'
                            }`}
                        >
                            <Settings className="w-4 h-4 inline mr-2" />
                            System
                        </button>
                        <button
                            onClick={() => setActiveTab('moderation')}
                            className={`py-4 px-1 border-b-2 font-medium text-sm ${
                                activeTab === 'moderation'
                                    ? 'border-blue-500 text-blue-600'
                                    : 'border-transparent text-gray-500 hover:text-gray-700'
                            }`}
                        >
                            <ShieldCheckIcon className="w-4 h-4 inline mr-2" />
                            Moderation
                        </button>
                    </nav>
                </div>
            </div>

            {/* Main Content */}
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                {/* Dashboard Tab */}
                {activeTab === 'dashboard' && (
                    <div className="space-y-6">
                        <h2 className="text-lg font-semibold text-gray-900">Dashboard Overview</h2>

                        {/* Stats Grid */}
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                            <StatCard title="Total Users" value={analytics.totalUsers?.toLocaleString()} icon={Users} />
                            <StatCard title="Active Users" value={analytics.activeUsers?.toLocaleString()} icon={UserCheck} />
                            <StatCard title="Monthly Revenue" value={`${(analytics.monthlyRecurring / 100)?.toLocaleString()}`} icon={DollarSign} />
                            <StatCard title="Flagged Users" value={analytics.flaggedUsers} icon={AlertTriangle} color="yellow" />
                        </div>

                        {/* Plan Distribution */}
                        <div className="bg-white rounded-lg shadow p-6">
                            <h3 className="text-lg font-medium text-gray-900 mb-4">Plan Distribution</h3>
                            <div className="space-y-3">
                                <div className="flex justify-between items-center">
                                    <span className="text-sm text-gray-600">Free</span>
                                    <span className="text-sm font-medium">{analytics.planDistribution?.free || 0}</span>
                                </div>
                                <div className="flex justify-between items-center">
                                    <span className="text-sm text-gray-600">Pro</span>
                                    <span className="text-sm font-medium">{analytics.planDistribution?.pro || 0}</span>
                                </div>
                                <div className="flex justify-between items-center">
                                    <span className="text-sm text-gray-600">Enterprise</span>
                                    <span className="text-sm font-medium">{analytics.planDistribution?.enterprise || 0}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Users Tab */}
                {activeTab === 'users' && (
                    <div className="space-y-6">
                        <div className="flex justify-between items-center">
                            <h2 className="text-lg font-semibold text-gray-900">User Management</h2>
                            <button
                                onClick={() => exportData('users')}
                                className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                            >
                                <Download className="w-4 h-4 mr-2" />
                                Export Users
                            </button>
                        </div>

                        {/* Search and Filters */}
                        <div className="bg-white rounded-lg shadow p-6">
                            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                                <div className="relative">
                                    <Search className="absolute left-3 top-3 w-4 h-4 text-gray-400" />
                                    <input
                                        type="text"
                                        placeholder="Search users..."
                                        className="pl-10 w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                    />
                                </div>
                                <select
                                    className="p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    value={filters.plan}
                                    onChange={(e) => setFilters({...filters, plan: e.target.value})}
                                >
                                    <option value="all">All Plans</option>
                                    <option value="free">Free</option>
                                    <option value="pro">Pro</option>
                                    <option value="enterprise">Enterprise</option>
                                </select>
                                <select
                                    className="p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    value={filters.status}
                                    onChange={(e) => setFilters({...filters, status: e.target.value})}
                                >
                                    <option value="all">All Status</option>
                                    <option value="active">Active</option>
                                    <option value="blocked">Blocked</option>
                                    <option value="suspended">Suspended</option>
                                </select>
                            </div>
                        </div>

                        {/* Users Table */}
                        <div className="bg-white rounded-lg shadow overflow-hidden">
                            <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        User
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Plan
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Status
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Posts
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Last Login
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Flags
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Actions
                                    </th>
                                </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                {loading ? (
                                    <tr>
                                        <td colSpan="7" className="px-6 py-12 text-center text-gray-500">
                                            <div className="flex items-center justify-center">
                                                <RefreshCw className="w-6 h-6 animate-spin mr-2" />
                                                Loading users...
                                            </div>
                                        </td>
                                    </tr>
                                ) : filteredUsers.length === 0 ? (
                                    <tr>
                                        <td colSpan="7" className="px-6 py-12 text-center text-gray-500">
                                            No users found matching your criteria.
                                        </td>
                                    </tr>
                                ) : (
                                    filteredUsers.map((user) => (
                                        <UserRow key={user.id} user={user} />
                                    ))
                                )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}

                {/* Payments Tab */}
                {activeTab === 'payments' && (
                    <div className="space-y-6">
                        <div className="flex justify-between items-center">
                            <h2 className="text-lg font-semibold text-gray-900">Payment Management</h2>
                            <button
                                onClick={() => exportData('payments')}
                                className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                            >
                                <Download className="w-4 h-4 mr-2" />
                                Export Payments
                            </button>
                        </div>

                        {/* Payments Table */}
                        <div className="bg-white rounded-lg shadow overflow-hidden">
                            <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Payment ID
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        User Email
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Amount
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Plan
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Status
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Date
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Actions
                                    </th>
                                </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                {loading ? (
                                    <tr>
                                        <td colSpan="7" className="px-6 py-12 text-center text-gray-500">
                                            <div className="flex items-center justify-center">
                                                <RefreshCw className="w-6 h-6 animate-spin mr-2" />
                                                Loading payments...
                                            </div>
                                        </td>
                                    </tr>
                                ) : payments.length === 0 ? (
                                    <tr>
                                        <td colSpan="7" className="px-6 py-12 text-center text-gray-500">
                                            No payments found.
                                        </td>
                                    </tr>
                                ) : (
                                    payments.map((payment) => (
                                        <PaymentRow key={payment.id} payment={payment} />
                                    ))
                                )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}

                {activeTab === 'moderation' && (

                    <ModerationDashboard/>
                )}

                {/* System Tab */}
                {activeTab === 'system' && (
                    <div className="space-y-6">
                        <h2 className="text-lg font-semibold text-gray-900">System Administration</h2>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            {/* System Actions */}
                            <div className="bg-white rounded-lg shadow p-6">
                                <h3 className="text-lg font-medium text-gray-900 mb-4">System Actions</h3>
                                <div className="space-y-3">
                                    <button className="w-full flex items-center justify-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors">
                                        <Database className="w-4 h-4 mr-2" />
                                        Backup Database
                                    </button>
                                    <button className="w-full flex items-center justify-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors">
                                        <RefreshCw className="w-4 h-4 mr-2" />
                                        Refresh Cache
                                    </button>
                                    <button className="w-full flex items-center justify-center px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 transition-colors">
                                        <Mail className="w-4 h-4 mr-2" />
                                        Send System Notification
                                    </button>
                                </div>
                            </div>

                            {/* System Health */}
                            <div className="bg-white rounded-lg shadow p-6">
                                <h3 className="text-lg font-medium text-gray-900 mb-4">System Health</h3>
                                <div className="space-y-3">
                                    <div className="flex justify-between items-center">
                                        <span className="text-sm text-gray-600">Database</span>
                                        <span className="text-sm font-medium text-green-600">Healthy</span>
                                    </div>
                                    <div className="flex justify-between items-center">
                                        <span className="text-sm text-gray-600">API</span>
                                        <span className="text-sm font-medium text-green-600">Operational</span>
                                    </div>
                                    <div className="flex justify-between items-center">
                                        <span className="text-sm text-gray-600">Stripe</span>
                                        <span className="text-sm font-medium text-green-600">Connected</span>
                                    </div>
                                    <div className="flex justify-between items-center">
                                        <span className="text-sm text-gray-600">Email Service</span>
                                        <span className="text-sm font-medium text-green-600">Active</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
                <UserDetailModal />
            </div>
        </div>
    );
};

export default AdminPortal;